import { AlertTriangle, CheckCircle2, CloudOff, RefreshCw, Trash2 } from "lucide-react";

import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { EmptyState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import type { BadgeTone } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatDateTime } from "@/lib/format";
import type { OutboxEntry, OutboxStatus } from "@/offline/outbox";
import { discard, drain, retry } from "@/offline/sync";
import { useOutbox } from "@/offline/useOutbox";

const STATUS: Record<OutboxStatus, { label: string; tone: BadgeTone }> = {
  PENDING: { label: "En attente de sync", tone: "reserved" },
  CONFLICT: { label: "Conflit", tone: "warning" },
  FAILED: { label: "Échec", tone: "out" },
};

/**
 * The queue, visible (Frontend Spec §25).
 *
 * Everything that has not reached the server, in the order it will be sent, with
 * what the server said when it refused. Nothing is ever dropped silently: an
 * operation leaves this list because it succeeded, or because a person decided
 * to abandon it.
 */
export function OutboxPage() {
  const { entries, online, state, lastSyncedAt } = useOutbox();
  const toast = useToast();

  return (
    <Page width="reading">
      <PageHeader
        title="File de synchronisation"
        subtitle="Ce qui n'a pas encore atteint le serveur. Rien n'est perdu."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => void drain()}
            disabled={!online || state === "SYNCING"}
            title={online ? undefined : "Aucun réseau pour l'instant."}
          >
            <RefreshCw
              className={`size-4 ${state === "SYNCING" ? "animate-spin" : ""}`}
              aria-hidden
            />
            Synchroniser
          </Button>
        }
      />

      <Card>
        <CardHeader
          title={online ? "Connecté" : "Hors ligne"}
          action={
            lastSyncedAt ? (
              <span className="text-[12px] text-ink-subtle">
                Dernière sync {formatDateTime(new Date(lastSyncedAt).toISOString())}
              </span>
            ) : undefined
          }
        />
        <CardBody>
          {entries.length === 0 ? (
            <EmptyState
              icon={
                online ? (
                  <CheckCircle2 className="size-6" aria-hidden />
                ) : (
                  <CloudOff className="size-6" aria-hidden />
                )
              }
              title={online ? "Tout est synchronisé" : "Rien en attente"}
              description={
                online
                  ? "Aucune opération en attente."
                  : "Vous êtes hors ligne, mais rien n'attend d'être envoyé."
              }
            />
          ) : (
            <ul className="flex flex-col divide-y divide-border/60">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <Row
                    entry={entry}
                    onRetry={async () => {
                      try {
                        await retry(entry.id);
                      } catch (caught) {
                        toast.error("Nouvel essai impossible", String(caught));
                      }
                    }}
                    onDiscard={async () => {
                      await discard(entry.id);
                      toast.info("Opération abandonnée", entry.label);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </Page>
  );
}

function Row({
  entry,
  onRetry,
  onDiscard,
}: {
  entry: OutboxEntry;
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const status = STATUS[entry.status];
  const refused = entry.status !== "PENDING";

  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium text-ink">{entry.label}</p>
          <p className="truncate text-[11px] text-ink-subtle">
            {formatDateTime(new Date(entry.createdAt).toISOString())}
            {entry.attempts > 1 ? ` · ${entry.attempts} tentatives` : ""}
            {entry.file ? " · photo jointe" : ""}
          </p>
        </div>
        <StatusBadge tone={status.tone} className="shrink-0">
          {status.label}
        </StatusBadge>
      </div>

      {entry.error && (
        <p className="flex items-start gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2 text-[12px] text-ink-muted">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-status-out" aria-hidden />
          <span className="min-w-0">
            {entry.error.message}
            {entry.error.code === "idempotency_conflict" && (
              <span className="mt-1 block text-ink-subtle">
                Cette opération a déjà été enregistrée sous une autre forme. La renvoyer
                telle quelle créerait un doublon : vérifiez l'écran concerné avant de décider.
              </span>
            )}
          </span>
        </p>
      )}

      {refused && (
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onRetry}>
            <RefreshCw className="size-4" aria-hidden />
            Réessayer
          </Button>
          <Button variant="ghost" size="sm" onClick={onDiscard}>
            <Trash2 className="size-4" aria-hidden />
            Abandonner
          </Button>
        </div>
      )}
    </div>
  );
}
